import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { Select } from '../../../../form/select';
import { Textarea } from '../../../../form/textarea';
import { TextInput } from '../../../../form/textInput';
import { operationType, selectDataType } from '../../../../../services/types';
import { Main, Styled } from './style';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../../expanded';

type BrowserUseProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

const DEFAULT_VALUE = {
   request: 'agent_decide',
   startUrl: '',
   allowedHosts: '',
   maxSteps: 20,
};

const OPERATION_OPTIONS: selectDataType[] = [
   { id: 1, name: 'Let AI Agent Decide', refId: 'agent_decide' },
   { id: 2, name: 'Navigate', refId: 'navigate' },
   { id: 3, name: 'View elements', refId: 'view' },
   { id: 4, name: 'Click element', refId: 'click_element' },
   { id: 5, name: 'Input text', refId: 'input_text' },
   { id: 6, name: 'Scroll', refId: 'scroll' },
   { id: 7, name: 'Screenshot', refId: 'screenshot' },
   { id: 8, name: 'Extract text', refId: 'extract' },
];

function clampMaxSteps(value: any) {
   const parsed = Number.parseInt(String(value ?? ''), 10);
   if (!Number.isFinite(parsed)) return 20;
   return Math.min(40, Math.max(1, parsed));
}

export function BrowserUse({ id, data, setChildMinimize, setChildSettings }: BrowserUseProps) {
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [operations, setOperations] = React.useState<selectDataType[]>(
      OPERATION_OPTIONS.map((item) => ({ ...item, isActive: item.refId === 'agent_decide' }))
   );

   const patchValue = React.useCallback((patch: Record<string, any>) => {
      setNodes((nds) => nds.map((node) => {
         if (node.id !== id) return node;
         return { ...node, data: { ...node.data, value: { ...DEFAULT_VALUE, ...node.data.value, ...patch } } };
      }));
   }, [id, setNodes]);

   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded((prev) => !prev); });
      if (setChildSettings) setChildSettings(() => { setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle })); });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   React.useEffect(() => {
      if (!data.value) {
         patchValue(DEFAULT_VALUE);
         return;
      }
      const nextRequest = data.value.request || 'agent_decide';
      const nextMaxSteps = clampMaxSteps(data.value.maxSteps);
      setOperations((items) => items.map((item) => ({ ...item, isActive: item.refId === nextRequest })));
      if (data.value.request !== nextRequest || Number(data.value.maxSteps) !== nextMaxSteps) {
         patchValue({ request: nextRequest, maxSteps: nextMaxSteps });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const handleSelectOperation = (selectId: number) => {
      const selected = OPERATION_OPTIONS.find((item) => item.id === selectId);
      setOperations((items) => items.map((item) => ({ ...item, isActive: item.id === selectId })));
      patchValue({ request: selected?.refId || 'agent_decide' });
   };

   const isWarning = false;

   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}

         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: '400px' }}
         ><Styled>
               <Select title="Operation" data={operations} handleSelectItem={handleSelectOperation} isSmall />
               <TextInput
                  title="Start URL"
                  value={data.value?.startUrl || ''}
                  placeholder="https://example.com"
                  handleChange={(e) => patchValue({ startUrl: e.target.value })}
               />
               <Textarea
                  title="Allowed hosts"
                  value={data.value?.allowedHosts || ''}
                  handleChange={(e) => patchValue({ allowedHosts: e.target.value })}
               />
               <TextInput
                  type="number"
                  title="Max steps"
                  value={String(data.value?.maxSteps ?? 20)}
                  placeholder="20"
                  min={1}
                  max={40}
                  isNormal={false}
                  handleChange={(e) => patchValue({ maxSteps: e.target.value === '' ? '' : clampMaxSteps(e.target.value) })}
               />
               <div className="guidance">
                  <p>Agent tool: connect the AI Agent Tools handle to this block so the agent can browse, click, type, and extract.</p>
                  <p>When you run the workflow, an Agent Browser panel opens on this page so you can watch the live page, clicks, and extracted text. Leave Allowed hosts empty to allow any public site, or list domains like quotes.toscrape.com.</p>
                  <p>After login, the agent should call view and click sidebar links such as RAG Studio. Do not use this block to search Google.</p>
               </div>
            </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}
